"use client";

import Link from "next/link";
import { api } from "@/trpc/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";

import { Button } from "@cohub42/ui/components/ui/button";
import { Card } from "@cohub42/ui/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@cohub42/ui/components/ui/form";
import { Input } from "@cohub42/ui/components/ui/input";
import { PhoneInput } from "@cohub42/ui/components/ui/phone-input";
import { Textarea } from "@cohub42/ui/components/ui/textarea";
import { useToast } from "@cohub42/ui/components/ui/use-toast";

// import { useToast } from "@cohub42/ui/hooks/use-toast";

const contactFormSchema = z.object({
  name: z.string().min(1, "Full name is required").max(100, "Name is too long"),
  email: z
    .string()
    .email("Please enter a valid email address")
    .max(254, "Email is too long"),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  message: z
    .string()
    .min(1, "Message is required")
    .max(5000, "Message is too long (max 5000 characters)"),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export default function ContactSection() {
  const { toast } = useToast();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const sendContactForm = api.share.sendContactForm.useMutation({
    onSuccess: () => {
      toast({
        title: "Message sent!",
        description: "Thank you for reaching out. We'll get back to you soon.",
      });
      form.reset();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to send message. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ContactFormValues) => {
    sendContactForm.mutate(data);
  };

  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <h1 className="mb-12 text-center text-4xl font-semibold lg:text-5xl">
          Contact Us
        </h1>

        <div className="grid divide-y border md:grid-cols-2 md:gap-4 md:divide-x md:divide-y-0">
          <div className="flex flex-col justify-between space-y-8 p-6 sm:p-12">
            <div>
              <h2 className="mb-3 text-lg font-semibold">Collaborate</h2>
              <Link
                href="mailto:hello@cohub42.com"
                className="text-lg text-blue-600 hover:underline dark:text-blue-400"
              >
                hello@cohub42.com
              </Link>
              <p className="mt-3 text-sm">+971 524655728</p>
            </div>
          </div>
          <div className="flex flex-col justify-between space-y-8 p-6 sm:p-12">
            <div>
              <h3 className="mb-3 text-lg font-semibold">Support</h3>
              <Link
                href="mailto:support@cohub42.com"
                className="text-lg text-blue-600 hover:underline dark:text-blue-400"
              >
                support@cohub42.com
              </Link>
              <p className="mt-3 text-sm">+971 524655728</p>
            </div>
          </div>
        </div>

        <div className="h-3 border-x bg-[repeating-linear-gradient(-45deg,var(--color-border),var(--color-border)_1px,transparent_1px,transparent_6px)]"></div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="border px-4 py-5 lg:px-0 lg:py-10"
          >
            <Card className="mx-auto max-w-lg p-8 sm:p-16">
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          disabled={sendContactForm.isPending}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          disabled={sendContactForm.isPending}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <PhoneInput
                          defaultCountry="AE"
                          placeholder="+971 00 000 0000"
                          disabled={sendContactForm.isPending}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={3}
                          placeholder="How can we help you?"
                          disabled={sendContactForm.isPending}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={sendContactForm.isPending}>
                  {sendContactForm.isPending ? "Sending..." : "Submit"}
                </Button>
              </div>
            </Card>
          </form>
        </Form>
      </div>
    </section>
  );
}
